import { Card, Center, Flex, Skeleton, Stack, Title, useMatches } from "@mantine/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import HistoricalAuctionEmptyState from "../EmptyStates/HistoricalAuctionEmptyState";
import { MarketEventAnalysis } from "../MarketEventAnalysis/MarketEventAnalysis";

import {
  EmptyState,
  EmptyStateRevamped,
  HammerPriceCard,
  useColumnSorter,
} from "@/components/elements";
import { AuctionsChart, TransactionsTable } from "@/components/widgets";
import { transactionsToChartData } from "@/components/widgets/AuctionsChart/utils";
import { useTransactionsTableData } from "@/components/widgets/TransactionsTable/useTransactionsTableData";
import { useAuctionMarketCarHammerPriceByConditions } from "@/services/auctions/get-auction-market-car-hammer-price-by-conditions";
import { useAuctionMarketCarHammerPriceByYear } from "@/services/auctions/get-auction-market-car-hammer-price-by-years";
import { useAuctionMarketCarTransactions } from "@/services/auctions/get-auction-market-car-transactions";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { SortActionDirection } from "@/components/elements";
import type { AuctionCarTransaction, AuctionCarTransactionColumns } from "@/types/auctions";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type AuctionAnalysisProps = {
  carId: string;
  defaultDirection?: SortActionDirection;
  limit?: number;
} & MantineStyleProps;

const namespace = "transactions-table";

export const AuctionAnalysis: FC<AuctionAnalysisProps> = ({
  carId,
  defaultDirection = "best",
  limit = 5,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobile = useMatches({ base: true, md: false });

  const { data: plans } = usePlansUser();

  const isFreeUser = plans?.name === "starter";

  const { sort } = useColumnSorter<AuctionCarTransaction, AuctionCarTransactionColumns>(
    `${namespace}-sort`,
    undefined
  );

  const {
    availableConditions,
    availableResults,
    clearAllFilters,
    currency,
    dateRange,
    handlePageSize,
    hasSourceTransactions,
    hasTableFilters,
    isFiltering,
    isLoading,
    page,
    pageSize,
    search,
    searchedTransactions,
    selectedConditions,
    selectedResults,
    selectTransaction,
    setConditionFilters,
    setDateRange,
    setOutliersVisibility,
    setPage,
    setReplicasVisibility,
    setResultFilters,
    showOutliers,
    showReplicas,
    tableTransactions,
    totalTransactions,
    updateSearchQuery,
  } = useTransactionsTableData({
    loadAll: true,
    marketCarId: carId,
    sort,
    useTransactions: useAuctionMarketCarTransactions,
  });

  const chartData = useMemo(
    () => transactionsToChartData(searchedTransactions),
    [searchedTransactions]
  );

  const visibleChartDataLength = useMemo(
    () => transactionsToChartData(searchedTransactions).length,
    [searchedTransactions]
  );

  const { data: hammerPriceByConditions, isLoading: conditionsIsLoading } =
    useAuctionMarketCarHammerPriceByConditions({ marketCarId: carId });
  const { data: hammerPriceByYear, isLoading: yearIsLoading } =
    useAuctionMarketCarHammerPriceByYear({
      marketCarId: carId,
    });

  const showNoTransactionData =
    !isLoading && !hasSourceTransactions && !hasTableFilters && !isFiltering;

  const showUnifiedEmptyState =
    !isLoading &&
    hasSourceTransactions &&
    hasTableFilters &&
    !isFiltering &&
    visibleChartDataLength === 0 &&
    totalTransactions === 0;

  const auctionsChartProps = {
    availableConditions,
    availableResults,
    carId,
    data: chartData,
    dateRange,
    hasActiveFilters: hasTableFilters,
    hasSourceData: hasSourceTransactions,
    onCancelAllFilters: clearAllFilters,
    onConditionsChange: setConditionFilters,
    onPointClick: selectTransaction,
    onResultsChange: setResultFilters,
    onShowOutliersChange: setOutliersVisibility,
    onShowReplicasChange: setReplicasVisibility,
    onTimeframeChange: setDateRange,
    selectedConditions,
    selectedResults,
    showOutliers,
    showReplicas,
    withTrendline: false,
  };

  const transactionsTableColumns: AuctionCarTransactionColumns[] = [
    "date",
    "hammer_price",
    "listing_price",
    "net_to_seller",
    "condition",
    "production_year",
    "vin",
    "result",
    "auction_house",
    "event_region",
    "event_country",
    "outlier",
    "replica",
    "more",
  ];

  const transactionsTableProps = {
    columns: transactionsTableColumns,
    hasActiveFilters: hasTableFilters,
    marketCarId: carId,
    namespace,
    onPageChange: setPage,
    onPageSizeChange: handlePageSize,
    onSearch: updateSearchQuery,
    searchValue: search,
    page,
    pageSize,
    totalTransactions,
    transactions: tableTransactions,
  };

  const hasCardData = !!hammerPriceByConditions || !!hammerPriceByYear;

  const hasInitialData =
    hasCardData ||
    hasSourceTransactions ||
    totalTransactions > 0 ||
    chartData.length > 0 ||
    hasTableFilters;

  const withHammerPriceData =
    (hammerPriceByConditions && hammerPriceByConditions?.best.length > 0) ||
    (hammerPriceByConditions && hammerPriceByConditions?.worst.length > 0);

  const withHammerPriceByYearData =
    (hammerPriceByConditions && hammerPriceByConditions?.best.length > 0) ||
    (hammerPriceByConditions && hammerPriceByConditions?.worst.length > 0);

  return (
    <Stack>
      {!hasInitialData ? (
        <Card p="lg">
          <Stack>
            <Title order={3}>{t("car.auctions_analysis_tab.average_hammer_price")}</Title>
            <EmptyState type="noData" text={t("common.noData")} h={265} />
          </Stack>
        </Card>
      ) : (
        <>
          <MarketEventAnalysis
            carId={carId}
            years={3}
            currency={currency}
            source="auctions"
            cols={{ base: 2, md: 4 }}
          />

          <Flex
            direction={isMobile ? "column" : "row"}
            justify={isMobile ? "flex-start" : "space-between"}
            align="stretch"
            gap="lg"
            w="100%"
            {...props}
          >
            <Card w="100%">
              <Title order={4} opacity={withHammerPriceData ? 1 : 0.3}>
                {t("car.auctions_analysis_tab.average_hammer_price_by_conditions")}
              </Title>
              <HammerPriceCard
                data={hammerPriceByConditions}
                defaultTimeframe="all"
                loading={conditionsIsLoading}
                enableContentLocker={isFreeUser}
                emptyStateTitle={t("car.auctions_analysis_tab.average_hammer_price_by_conditions")}
                emptyStateVariant="conditions"
              />
            </Card>

            <Card w="100%">
              <Title order={4} opacity={withHammerPriceByYearData ? 1 : 0.3}>
                {t("car.auctions_analysis_tab.avarage_hammer_price_by_year")}
              </Title>
              <HammerPriceCard
                data={hammerPriceByYear}
                defaultTimeframe="all"
                loading={yearIsLoading}
                enableContentLocker={isFreeUser}
                emptyStateTitle={t("car.auctions_analysis_tab.avarage_hammer_price_by_year")}
                emptyStateVariant="year"
              />
            </Card>
          </Flex>

          <Card>
            <Stack>
              <Skeleton visible={isLoading}>
                <AuctionsChart
                  {...auctionsChartProps}
                  suppressEmptyState={showNoTransactionData || showUnifiedEmptyState}
                />
              </Skeleton>

              {showNoTransactionData ? (
                <HistoricalAuctionEmptyState />
              ) : showUnifiedEmptyState ? (
                <Center h="20em">
                  <EmptyStateRevamped />
                </Center>
              ) : (
                <Skeleton visible={isLoading}>
                  <TransactionsTable {...transactionsTableProps} />
                </Skeleton>
              )}
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
};
